import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TrevorIcon, Tooltip } from '@trevor/ui-components';
import { get, post, userAction } from '@/util/helpers/ajax-helpers';
import Map, { FIELD_HEIGHT, TITLE_HEIGHT } from './Map';
import SchemaTable from '../../util/SchemaTable';
import SchemaColumn from '../../util/SchemaColumn';
import AjaxSpinner from '../AjaxSpinner';
import { getFullTableName, SchemaEntitiesList } from '../SchemaEntitiesList';
import ModalContent from '../ModalContent';
import SelectJoins from '../configure/SelectJoins';
import SelectTables_LEGACY from '../configure/SelectTables_LEGACY';
import ResizeableSidebar from '../ResizeableSidebar';
import SaveBar from '../SaveBar';
import style from '../../css/module/er-diagram.css';
import flex from '../../css/module/flex.css';
import cssNewWorld from '../../css/module/new-world.css';
import buttons from '../../css/module/buttons.css';
import ColumnRef from '../../util/ColumnRef';
import MapsTable from '../../util/MapsTable';
import DropdownItem from '../../util/DropdownItem';
import MultiDropdown from '../MultiDropdown';
import DropdownButton from '../DropdownButton';
import CopyIcon from '../CopyIcon';
import {
  closeReactModal,
  loadTables,
  loadVisibleTables,
  openReactModal,
  updateScale,
  updateVisibleTables,
} from '../../actions';
import { playPuck } from '../../util/Sounds';
import { SchemaEntitiesSearch } from './subcomponents/SchemaEntitiesSearch';

const VIEWPORT_MARGIN = 100;
const NEW_TABLE_ESTIMATED_WIDTH = 100; // some arbitrary width (as we don't actually know how wide new table will be until we add it)

const plusMinusButtonStyle = {
  padding: '8px 0px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

class MapContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      joins: null, // list of Pairs of ColumnRefs
      tables: null, // list of all tables - SchemaTable objs
      sidebarOpen: true,
      wrapSidebar: false,
      scrollPosition: { top: -1, left: -1 },
      isSaving: false,
    };
    this.onAddTable = this.onAddTable.bind(this);
    this.discardChanges = this.discardChanges.bind(this);
    this.onToggleSidebar = this.onToggleSidebar.bind(this);
    this.onWrapSidebar = this.onWrapSidebar.bind(this);
    this.onRemoveTable = this.onRemoveTable.bind(this);
    this.loadAllTablesAndJoins = this.loadAllTablesAndJoins.bind(this);
    this.dropdownBuilder = this.dropdownBuilder.bind(this);
  }

  componentDidMount() {
    if (!this.props.visibleTables) {
      this.props.onLoadVisibleTables();
    }
    this.loadAllTablesAndJoins();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.diagramHasChanged && !this.props.diagramHasChanged) {
      this.setState({
        isSaving: false,
      });
    }
  }

  loadAllTablesAndJoins() {
    get(
      'loading list of tables and columns',
      '/tables_and_columns?includeJoins=true',
    ).then((json) => {
      this.setState({
        joins: json.fks.map((fk) => [
          ColumnRef.buildFromServer(fk.a),
          ColumnRef.buildFromServer(fk.b),
        ]),
        tables: json.tables.map(
          (t) =>
            new SchemaTable(
              t.schema,
              t.table,
              t.columns.map((c) => new SchemaColumn(c._name, c._type)),
            ),
        ),
      });
    });
  }

  onSave() {
    this.setState({
      isSaving: true,
    });
    post('saving changes', '/maps', {
      tables: this.props.visibleTables,
    })
      .then(() => {
        this.props.onLoadVisibleTables(); // sets diagramHasChanged to false
        userAction('use-save-map', { table: this.props.visibleTables });
      })
      .catch(() => {
        this.setState({
          isSaving: false,
        });
      });
  }

  discardChanges() {
    this.setState({
      isSaving: true,
    });
    this.props.onLoadVisibleTables();
    userAction('use-discard-map-changes');
  }

  createMapsTable(table) {
    //
    // identify which columns to show by default
    //
    const visibleColumns = table
      .columns()
      .map((c) => c.name())
      .filter((column) => {
        if (
          column.toLowerCase().endsWith('id') ||
          column.toLowerCase().endsWith('at') ||
          column.toLowerCase().endsWith('date') ||
          column.toLowerCase().endsWith('stamp') ||
          column.toLowerCase().endsWith('name') ||
          column.toLowerCase().endsWith('type') ||
          column.toLowerCase().endsWith('state')
        ) {
          return true;
        }
        // check if is popular column.  See: https://app.trevor.io/share/v/b6ff6e33-0af2-4145-b14e-c2a69179b3a8
        if (
          [
            'email',
            'description',
            'value',
            'status',
            'version',
            'title',
            'key',
            'city',
            'country',
            'url',
            'amount',
            'active',
            'price',
          ].indexOf(column) > -1
        ) {
          return true;
        }
        // check if is fk
        const col = new ColumnRef(table.toTov(), column);
        return this.state.joins.some(
          ([a, b]) => a.equals(col) || b.equals(col),
        );
      });

    //
    // sort the columns into some reasonable order
    //
    const score = (col) => {
      if (col === 'id') {
        return 1; // primary key comes first
      }
      if (
        col.endsWith('id') &&
        col.toLowerCase().includes(table.table().toLowerCase().slice(0, -1))
      ) {
        return 2; // alternative primary key naming
      }
      if (col.toLowerCase().endsWith('at')) {
        return 3; // then date cols
      }
      if (col.toLowerCase().endsWith('id')) {
        return 4; // then fks
      }
      return 5; // then everything else
    };
    visibleColumns.sort((a, b) => score(a) - score(b));

    //
    // find a spot to put the table
    //
    const coords = this.findSpace(visibleColumns.length);

    return new MapsTable(
      table.schema(),
      table.table(),
      coords.x,
      coords.y,
      visibleColumns,
    );
  }

  onAddTable(table) {
    userAction('use-add-table-to-map', {
      table: table.table(),
      schema: table.schema(),
    });
    const { visibleTables, onUpdateVisibleTables } = this.props;
    onUpdateVisibleTables(true)([
      ...visibleTables,
      this.createMapsTable(table),
    ]);
    playPuck();
  }

  onRemoveTable(table) {
    const { visibleTables, onUpdateVisibleTables } = this.props;
    const indexToRemove = visibleTables.findIndex(
      (t) => t.schema() === table.schema() && t.table() === table.table(),
    );

    onUpdateVisibleTables(true)([
      ...visibleTables.slice(0, indexToRemove),
      ...visibleTables.slice(indexToRemove + 1),
    ]);
  }

  addAll(i = 0) {
    const { tables } = this.state;
    if (i >= tables.length) {
      return;
    }
    this.onAddTable(tables[i]);
    setTimeout(() => {
      this.addAll(i + 1);
    }, 1);
  }

  /**
   * The size of the area that is currently visible of the map
   */
  viewPortDims() {
    const rect = this._map._wrapper.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
    };
  }

  /**
   * The area in which we will add any new tables (middle of the current viewport)
   */
  centralVisibleArea(tableHeight = VIEWPORT_MARGIN) {
    const { scale } = this.props;
    const viewPortDims = this.viewPortDims();
    const viewPortWidth =
      viewPortDims.width / scale -
      2 * VIEWPORT_MARGIN -
      NEW_TABLE_ESTIMATED_WIDTH; // we subtract NEW_TABLE_ESTIMATED_WIDTH so that if table gets added on right edge of viewport, it still stays inside visible area
    const viewPortHeight =
      viewPortDims.height / scale - 2 * VIEWPORT_MARGIN - tableHeight / 2; // we subtract tableHeight/2 so that if table gets added on bottom edge of viewport, it still stays inside visible area
    const viewPortTop = VIEWPORT_MARGIN + this.state.scrollPosition.top / scale;
    const viewPortLeft =
      VIEWPORT_MARGIN + this.state.scrollPosition.left / scale;
    return {
      top: viewPortTop,
      left: viewPortLeft,
      width: viewPortWidth,
      height: viewPortHeight,
    };
  }

  findSpace(noOfColumns) {
    const { visibleTables } = this.props;

    const TABLE_WIDTH = NEW_TABLE_ESTIMATED_WIDTH;
    const TABLE_HEIGHT = TITLE_HEIGHT + noOfColumns * FIELD_HEIGHT;

    const visibleArea = this.centralVisibleArea(TABLE_HEIGHT);

    // loop through container and check if table being inserted would overlap any existing tables
    for (let i = 0; i < 100; i++) {
      // coordinates of position being checked
      const coords = {
        x: visibleArea.left + Math.abs(Math.random() * visibleArea.width),
        y: visibleArea.top + Math.abs(Math.random() * visibleArea.height),
      };

      // new tables position values if added in current position
      const newTable = this.position(
        coords.x,
        coords.y,
        TABLE_WIDTH,
        TABLE_HEIGHT,
      );

      // check if any of the visible tables are between the coords
      const overlaps = visibleTables.some((table) => {
        // dimensions of existing table
        const boxWidth = this._map.getTableWidth(table.schema(), table.table());
        const height =
          TITLE_HEIGHT + table.visibleColumns().length * FIELD_HEIGHT;
        const width = boxWidth / this.props.scale;

        // existing table position values
        const existingTable = this.position(
          table.x(),
          table.y(),
          width,
          height,
        );

        // check if either of the tables overlap each other
        const overlap =
          this.checkOverlap(existingTable, newTable) || // returns if true otherwise tests other one
          this.checkOverlap(newTable, existingTable);

        return overlap;
      });

      // if it's not, return the coords
      if (!overlaps) {
        return coords;
      }
    }

    // no space found if this point reached, so return the top left
    return {
      x: visibleArea.left + Math.abs(Math.random() * visibleArea.width),
      y: visibleArea.top + Math.abs(Math.random() * visibleArea.height),
    };
  }

  position(x, y, width, height) {
    return {
      left: x,
      right: x + width,
      top: y,
      bottom: y + height,
    };
  }

  checkOverlap(toCheck, toCheckAgainst) {
    return (
      toCheck.left <= toCheckAgainst.right &&
      toCheck.right >= toCheckAgainst.left &&
      toCheck.bottom >= toCheckAgainst.top &&
      toCheck.top <= toCheckAgainst.bottom
    );
  }

  onToggleSidebar(value) {
    this.setState({
      sidebarOpen: value,
    });
  }

  onWrapSidebar(value) {
    this.setState({
      wrapSidebar: value,
    });
  }

  /**
   * this just shows viewport (useful for debugging)
   */
  redBox() {
    if (!this._map) {
      return {};
    }
    const visibleArea = this.centralVisibleArea();
    const result = {
      position: 'absolute',
      border: 'solid 1px red',
      width: `${visibleArea.width}px`,
      height: `${visibleArea.height}px`,
      top: `${visibleArea.top}px`,
      left: `${visibleArea.left}px`,
    };
    return result;
  }

  zoom(adjustment) {
    const { onUpdateScale } = this.props;

    const newScale = this.props.scale + adjustment;

    // calculate the adjustment to the middle of the viewport (so it looks like we're zooming in and out from the middle of the image)
    const viewPortDims = this.viewPortDims();
    const middle = {
      x: this.state.scrollPosition.left + viewPortDims.width / 2,
      y: this.state.scrollPosition.top + viewPortDims.height / 2,
    };
    const newMiddle = {
      x: (middle.x * newScale) / this.props.scale,
      y: (middle.y * newScale) / this.props.scale,
    };
    // eslint-disable-next-line react/no-access-state-in-setstate
    const newTop = this.state.scrollPosition.top + (newMiddle.y - middle.y);
    // eslint-disable-next-line react/no-access-state-in-setstate
    const newLeft = this.state.scrollPosition.left + (newMiddle.x - middle.x);

    // update the new scale and new viewport positions
    this.setState({
      scrollPosition: {
        top: newTop,
        left: newLeft,
      },
    });
    onUpdateScale(newScale);
  }

  dropdownBuilder(table, onEditClicked) {
    const { isAdmin, onOpenConfigureJoinsModal } = this.props;

    const rearrangeCols = DropdownItem.action(
      <span>Rearrange columns</span>,
      onEditClicked,
    );
    const configureJoins = !isAdmin
      ? null
      : DropdownItem.action(
          <span>Configure joins</span>,
          onOpenConfigureJoinsModal,
        );
    const divider = DropdownItem.divider();
    const remove = DropdownItem.action(<span>Remove from map</span>, () =>
      this.onRemoveTable(table),
    );
    const menuItems = [rearrangeCols, configureJoins, divider, remove];

    return (
      <DropdownButton
        contentBuilder={() => <MultiDropdown items={menuItems} />}
      >
        <div className={style.boxControl}>
          <FontAwesomeIcon icon="ellipsis-v" />
        </div>
      </DropdownButton>
    );
  }

  render() {
    if (!this.state.tables || !this.props.visibleTables) {
      return (
        <div className={flex.middle}>
          <AjaxSpinner />
        </div>
      );
    }

    const {
      isAdmin,
      isSelfHosted,
      onOpenConfigureJoinsModal,
      onOpenConfigureTablesModal,
      onUpdateVisibleTables,
    } = this.props;

    const openConfigureTableModalButton =
      isAdmin && !isSelfHosted ? (
        <Button
          variant="secondary"
          size="small"
          onClick={onOpenConfigureTablesModal}
          icon={<TrevorIcon name="locked" />}
          style={{ marginRight: '8px' }}
        >
          Table access
        </Button>
      ) : null;

    const openConfigureJoinsModalButton = isAdmin ? (
      <Button
        variant="secondary"
        size="small"
        onClick={onOpenConfigureJoinsModal}
        icon={<TrevorIcon name="locked" />}
      >
        Configure joins
      </Button>
    ) : null;

    return (
      <div className={style.pageContainer}>
        <ResizeableSidebar
          isVisible
          isToggleShown
          initialWidth={300}
          sidebarOpen={this.state.sidebarOpen}
          wrapSidebar={this.state.wrapSidebar}
          onToggleSidebar={this.onToggleSidebar}
          onWrapSidebar={this.onWrapSidebar}
        >
          {this.state.tables && (
            <SchemaEntitiesSearch
              openConfigureTableModalButton={openConfigureTableModalButton}
              openConfigureJoinsModalButton={openConfigureJoinsModalButton}
            >
              <SchemaEntitiesList
                tables={this.state.tables}
                visibleTablesNames={this.props.visibleTables.map(
                  getFullTableName,
                )}
                onAddTable={this.onAddTable}
              />
            </SchemaEntitiesSearch>
          )}
        </ResizeableSidebar>
        <div className={style.mapContainer}>
          {this.props.visibleTables.length > 0 ? null : (
            <div className={style.onboardingMessageContainer}>
              <div className={`${flex.column} ${cssNewWorld.welcome}`}>
                <div className={`${flex.row} ${flex.alignItemsCenter}`}>
                  <h1>Welcome to the map!</h1>
                  <img
                    src="/img/compass.png"
                    style={{ width: '4em', marginLeft: '10px' }}
                  />
                </div>
                <h4>
                  Get started by <b>adding a table</b> from the left menu
                </h4>
              </div>
            </div>
          )}
          <Map
            key={this.props.mapKey}
            ref={(map) => (this._map = map)}
            redBox={this.redBox()}
            gridWidth={4000}
            gridHeight={3000}
            allTables={this.state.tables}
            visibleTables={this.props.visibleTables}
            joins={this.state.joins}
            scale={this.props.scale}
            dropdownBuilder={this.dropdownBuilder}
            scrollPosition={this.state.scrollPosition}
            onScroll={(pos) => this.setState({ scrollPosition: pos })}
            onChange={onUpdateVisibleTables(false)}
          />
          <div
            className={`${style.zoomButtons} ${cssNewWorld.hiddenDuringPrint}`}
          >
            <div
              className={`${buttons.verticalGroup} box-shadow ${buttons.rounded}`}
              style={{ color: '#000' }}
            >
              <button
                disabled={this.props.scale >= 1.0}
                onClick={() => this.zoom(+0.1)}
                className={buttons.btn}
                style={plusMinusButtonStyle}
                data-tooltip-id="zoom_in"
              >
                <TrevorIcon name="plus" />
                <Tooltip id="zoom_in">Zoom in</Tooltip>
              </button>
              <button
                disabled={this.props.scale <= 0.5}
                onClick={() => this.zoom(-0.1)}
                className={buttons.btn}
                style={plusMinusButtonStyle}
                data-tooltip-id="zoom_out"
              >
                <TrevorIcon name="minus" />

                <Tooltip id="zoom_out">Zoom out</Tooltip>
              </button>
            </div>
            <div
              className="margin-top-5 box-shadow"
              style={{ borderRadius: '50%' }}
            >
              <CopyIcon
                id="shareCopyLink"
                tooltip="Share with team"
                action="use-copy-map-url"
                icon="link"
                isMap
              />
            </div>
          </div>
          {!this.props.diagramHasChanged ? null : (
            <SaveBar
              isSaving={this.state.isSaving}
              onSave={(e) => this.onSave(e)}
              onCancel={this.discardChanges}
            />
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    diagramHasChanged: state.map.diagramHasChanged,
    visibleTables: state.map.visibleTables,
    mapKey: state.map.mapKey,
    scale: state.map.scale,
    isAdmin: state.datasource.isAdmin,
    isSelfHosted: state.datasource.isSelfHosted,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onOpenConfigureTablesModal: () => {
      const closeModal = () => {
        dispatch(closeReactModal());
      };
      const reloadSchema = () => {
        dispatch(loadTables(false));
      };
      dispatch(
        openReactModal(
          <ModalContent onClose={closeModal} medium>
            <div style={{ height: '100%' }}>
              <SelectTables_LEGACY
                closeModal={closeModal}
                isModal
                reloadSchema={reloadSchema}
              />
            </div>
          </ModalContent>,
        ),
      );
    },
    onOpenConfigureJoinsModal: () => {
      const closeModal = () => {
        dispatch(closeReactModal());
      };
      const reloadSchema = () => {
        dispatch(loadTables(false));
      };
      dispatch(
        openReactModal(
          <ModalContent onClose={closeModal} medium>
            <div style={{ height: '100%' }}>
              <SelectJoins
                closeModal={closeModal}
                isModal
                onSaved={reloadSchema}
              />
            </div>
          </ModalContent>,
        ),
      );
    },
    onLoadVisibleTables: () => {
      dispatch(loadVisibleTables());
    },
    onUpdateVisibleTables: (rebuildMap) => (updated) => {
      dispatch(updateVisibleTables(updated, rebuildMap));
    },
    onUpdateScale: (scale) => {
      dispatch(updateScale(scale));
    },
  };
};

// note: need to do 'connect' here, so that we have access to 'dispatch' in componentDidMount
export default connect(mapStateToProps, mapDispatchToProps)(MapContainer);
